import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import type { WizardState, SessionConflict } from '../../hooks/useSessionWizard';
import { transitionSession } from '../../services/sessionTransitions';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
  wizard: {
    state: WizardState;
    advanceToContract: (actType: string) => Promise<SessionConflict | void>;
  };
}

const ACT_TYPES = [
  { value: 'tattoo',       fr: 'Tatouage',     icon: '🎨' },
  { value: 'piercing',     fr: 'Piercing',     icon: '💎' },
  { value: 'microblading', fr: 'Microblading', icon: '✏️' },
  { value: 'touch_up',     fr: 'Retouche',     icon: '🔄' },
  { value: 'other',        fr: 'Autre',        icon: '⚡' },
];

const STATUS_LABEL: Record<string, string> = {
  in_progress: 'En cours',
  paused: 'En pause',
};

export default function Step2ActType({ wizard }: Props) {
  const navigate = useNavigate();
  const [loading, setLoading]     = useState(false);
  const [closing, setClosing]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [conflict, setConflict]   = useState<SessionConflict | null>(null);
  const [pendingActType, setPendingActType] = useState('');

  const handleSelect = async (actType: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await wizard.advanceToContract(actType);
      if (result && (result as SessionConflict).conflict) {
        setConflict(result as SessionConflict);
        setPendingActType(actType);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Erreur lors de la création de la session');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAndNew = async () => {
    if (!conflict) return;
    setClosing(true);
    try {
      await transitionSession(conflict.sessionId, 'completed');
      setConflict(null);
      await handleSelect(pendingActType);
    } catch (err: any) {
      setError(err?.message ?? 'Erreur lors de la clôture');
      setConflict(null);
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold font-display mb-1">Type d'acte</h2>
        <p className="text-sm text-muted-foreground">Quel type de prestation allez-vous réaliser ?</p>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {ACT_TYPES.map((act) => (
          <button
            key={act.value}
            onClick={() => handleSelect(act.value)}
            disabled={loading || closing}
            className="glass-card p-5 flex flex-col items-center gap-2 hover:border-primary hover:bg-primary/5 transition-all duration-200 disabled:opacity-50"
          >
            <span className="text-3xl">{act.icon}</span>
            <span className="font-semibold text-sm">{act.fr}</span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 size={16} className="animate-spin" />
          Création de la session…
        </div>
      )}

      <Dialog open={!!conflict} onOpenChange={(open) => { if (!open && !closing) setConflict(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              Session active détectée
            </DialogTitle>
            <DialogDescription className="sr-only">Un conflit de session active a été détecté pour ce client</DialogDescription>
          </DialogHeader>
          {conflict && (
            <div className="space-y-4 pt-1">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{conflict.clientName}</span> a déjà une session{' '}
                <span className="font-semibold text-amber-500">
                  {STATUS_LABEL[conflict.status] ?? conflict.status}
                </span>.
              </p>
              <div className="space-y-2">
                <Button className="w-full" onClick={() => navigate(`/sessions/${conflict.sessionId}`)}>
                  Reprendre la session
                </Button>
                <Button variant="outline" className="w-full gap-2" disabled={closing} onClick={handleCloseAndNew}>
                  {closing && <Loader2 size={14} className="animate-spin" />}
                  Clôturer et nouvelle session
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
